// src/pages/ProfilePage.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RecurringOrdersSection from '../components/account/RecurringOrdersSection';
import { validateBusinessIdLoose } from '../utils/businessId'; // (lisätty) Business ID util

// Sama env-mekaniikka kuin AuthContextissa
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const apiUrl = (path) => (API_BASE ? `${API_BASE}${path}` : path);
const PROFILE_ENDPOINT = apiUrl('/api/profile');

const ALLOWED_CITIES = [
  { value: 'espoo', label: 'Espoo' },
  { value: 'helsinki', label: 'Helsinki' },
  { value: 'vantaa', label: 'Vantaa' },
  { value: 'kauniainen', label: 'Kauniainen' },
  { value: 'kirkkonummi', label: 'Kirkkonummi' },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isReady, updateUser, logout, getAuthHeaders } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [form, setForm] = useState({
    name: '',
    phone: '',
    businessId: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    email: '',
  });

  useEffect(() => {
    if (!isReady) return;
    if (!user) {
      navigate('/login', { replace: true, state: { from: '/account' } });
      return;
    }
    (async () => {
      setLoading(true);
      setError('');
      setOk('');
      try {
        const { data } = await axios.get(PROFILE_ENDPOINT, {
          headers: { ...getAuthHeaders() },
        });
        const profile = data?.user || data?.profile || data || {};
        setForm({
          name: profile.name || user.name || '',
          phone: profile.phone || user.phone || '',
          businessId: profile.businessId || '',
          streetAddress: profile.streetAddress || '',
          postalCode: profile.postalCode || '',
          city: (profile.city || '').toString().toLowerCase(),
          email: profile.email || user.email || '',
        });
      } catch (_) {
        setForm((prev) => ({
          ...prev,
          name: user.name || prev.name,
          phone: user.phone || prev.phone,
          email: user.email || prev.email,
        }));
        setError('Profiilin haku epäonnistui (näytetään tallennetut tiedot).');
      } finally {
        setLoading(false);
      }
    })();
  }, [isReady, user, navigate, getAuthHeaders]);

  const canSave = useMemo(() => !saving && !!user, [saving, user]);

  const handleChange = (e) => {
    setOk('');
    setError('');
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validateClient = () => {
    if (form.postalCode && !/^\d{5}$/.test(String(form.postalCode).trim())) {
      return 'Postinumero: käytä 5 numeroa (esim. 00100).';
    }
    if (form.city && !ALLOWED_CITIES.some((c) => c.value === String(form.city).toLowerCase())) {
      return 'Kaupunki on palvelualueen ulkopuolella.';
    }
    // (lisätty) Business ID -tarkistus utililla: virhe vain jos FI-muotoinen ja/tai util hylkää
    if (form.businessId) {
      const res = validateBusinessIdLoose(form.businessId);
      if (!res.ok) {
        // Jos util epäonnistuu FI-tarkisteesta → kerrotaan se; muuten yleinen muotovirhe
        return res.reason === 'checksum'
          ? 'Y-tunnuksen tarkistus ei täsmää (muoto 1234567-8).'
          : 'Y-tunnus / tunniste on väärässä muodossa.';
      }
    }
    return '';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    const clientErr = validateClient();
    if (clientErr) {
      setError(clientErr);
      return;
    }

    setSaving(true);
    setError('');
    setOk('');
    try {
      const payload = {};
      if (form.name !== '') payload.name = form.name;
      if (form.phone !== '') payload.phone = form.phone;
      if (form.businessId !== '') {
        // (muutettu) normalisoi Business ID utilin avulla ennen tallennusta
        const v = validateBusinessIdLoose(form.businessId);
        payload.businessId = v.ok ? (v.normalized || String(form.businessId).trim()) : String(form.businessId).trim();
      }
      if (form.streetAddress !== '') payload.streetAddress = form.streetAddress;
      if (form.postalCode !== '') payload.postalCode = form.postalCode;
      if (form.city !== '') payload.city = form.city.toLowerCase();

      // PUT ensisijaisesti…
      try {
        await axios.put(PROFILE_ENDPOINT, payload, { headers: { ...getAuthHeaders() } });
      } catch (err) {
        // …mutta jos backend antaa 405, kokeile PATCHia
        if (err?.response?.status === 405) {
          await axios.patch(PROFILE_ENDPOINT, payload, { headers: { ...getAuthHeaders() } });
        } else {
          throw err;
        }
      }

      updateUser({
        name: payload.name ?? form.name,
        phone: payload.phone ?? form.phone,
        businessId: payload.businessId ?? form.businessId,
        streetAddress: payload.streetAddress ?? form.streetAddress,
        postalCode: payload.postalCode ?? form.postalCode,
        city: payload.city ?? form.city,
      });

      setOk('Profiili tallennettu.');
    } catch (e2) {
      setError(e2?.response?.data?.error || e2?.response?.data?.message || 'Tallennus epäonnistui.');
    } finally {
      setSaving(false);
    }
  };

  if (!isReady) {
    return (
      <div className="account-page" style={{ padding: '24px' }}>
        <h1>Oma tili</h1>
        <p>Ladataan…</p>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="account-page" style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px 64px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Oma tili</h1>
        {!!location.state?.justRegistered && <span style={{ opacity: 0.7 }}>(Tervetuloa!)</span>}
      </div>
      <p style={{ margin: '6px 0 18px', opacity: 0.9 }}>
        Muokkaa yhteystietojasi. Sähköposti on kirjautumistunnuksesi.
      </p>

      <form onSubmit={handleSave} style={{ display: 'grid', gap: 14 }}>
        <Field label="Yrityksen nimi">
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Yritys Oy"
            style={inputStyle}
            disabled={loading}
          />
        </Field>

        <Field label="Puhelin">
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+358 ..."
            style={inputStyle}
            disabled={loading}
          />
        </Field>

        <Field label="Y-tunnus (valinnainen)">
          <input
            type="text"
            name="businessId"
            value={form.businessId}
            onChange={handleChange}
            placeholder="1234567-8"
            style={inputStyle}
            disabled={loading}
          />
        </Field>

        <Field label="Katuosoite (valinnainen)">
          <input
            type="text"
            name="streetAddress"
            value={form.streetAddress}
            onChange={handleChange}
            placeholder="Esimerkkikatu 1 A 1"
            style={inputStyle}
            disabled={loading}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
          <Field label="Postinumero (valinnainen)">
            <input
              type="text"
              name="postalCode"
              value={form.postalCode}
              onChange={handleChange}
              placeholder="00100"
              style={inputStyle}
              disabled={loading}
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
            />
          </Field>

          <Field label="Kaupunki (palvelualue)">
            <select
              name="city"
              value={form.city}
              onChange={(e) =>
                handleChange({ target: { name: 'city', value: e.target.value.toLowerCase() } })
              }
              style={{ ...inputStyle, height: 42 }}
              disabled={loading}
            >
              <option value="">— Valitse —</option>
              {ALLOWED_CITIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Sähköposti">
          <input
            type="email"
            name="email"
            value={form.email}
            readOnly
            style={{ ...inputStyle, background: '#f8f8f8' }}
          />
          <small style={{ opacity: 0.7 }}>Sähköpostia hallinnoidaan kirjautumisen kautta.</small>
        </Field>

        {error && <div style={errorStyle}>{error}</div>}
        {ok && <div style={okStyle}>{ok}</div>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="submit" disabled={!canSave} style={primaryBtnStyle}>
            {saving ? 'Tallennetaan…' : 'Tallenna tiedot'}
          </button>

          <Link to="/account/orders" style={secondaryBtnAsLinkStyle}>
            Näytä tilaushistoria
          </Link>

          <button
            type="button"
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
            style={ghostBtnStyle}
          >
            Kirjaudu ulos
          </button>
        </div>
      </form>

      {/* Toistuvat tilaukset 1–12 vk, suora muokkaus ja peruutus */}
      <div style={{ marginTop: 36 }}>
        <RecurringOrdersSection />
      </div>
    </div>
  );
}

/* -------------------- Label + sisältö -------------------- */
function Field({ label, children }) {
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <label style={{ fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

/* ---------------------------- Tyylejä ---------------------------- */
const inputStyle = {
  height: 40,
  borderRadius: 8,
  border: '1px solid #ddd',
  padding: '0 12px',
  outline: 'none',
};
const primaryBtnStyle = {
  height: 42,
  padding: '0 16px',
  borderRadius: 10,
  border: '1px solid #1b72ff',
  background: '#1b72ff',
  color: '#fff',
  cursor: 'pointer',
};
const secondaryBtnAsLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 42,
  padding: '0 16px',
  borderRadius: 10,
  border: '1px solid #ddd',
  background: '#fff',
  color: '#111',
  textDecoration: 'none',
};
const ghostBtnStyle = {
  height: 42,
  padding: '0 16px',
  borderRadius: 10,
  border: '1px solid #eee',
  background: 'transparent',
  color: '#444',
  cursor: 'pointer',
};
const errorStyle = {
  background: '#ffe9e9',
  color: '#b30000',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #ffcccc',
};
const okStyle = {
  background: '#ecfff1',
  color: '#0b7a3b',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #c7f0d8',
};
